/**
 * Заметки плана (DEV-1658).
 *
 * Заметку пишут во время разговора: одно поле, ни проекта, ни типа, ни срока.
 * Всё, что превращает мысль в задачу, разбирает потом ассистент — а любое
 * обязательное поле в момент записи это повод не записать вовсе.
 *
 * Разобранная заметка не удаляется: по ней потом восстанавливают, откуда взялась
 * задача, и ключи разбора лежат прямо в ней.
 */
import { planApi } from '@/api'
import { formatDueDate, intlLocale } from '@/features/issues/format'

import type { TrackerNote } from '@/api/clients/tracker-plan'

/** Заметка в том виде, в каком её рисует карточка */
export interface Note {
  id: string
  body: string
  /** Встреча, на которой записана; пусто у личной */
  sessionId: string
  /** Кто записал — у сессионной заметки автор не обязательно смотрящий */
  authorId: string
  authorName: string
  /** Момент разбора; пустой — заметка ещё просто заметка */
  processedAt: string
  /** Ключи задач, которые из неё вышли */
  issueKeys: string[]
  createdAt: string
}

const toNote = (note: TrackerNote): Note => ({
  id: note.id ?? '',
  body: note.bodyMd ?? '',
  sessionId: note.sessionId ?? '',
  authorId: note.authorId ?? '',
  authorName: note.authorName ?? '',
  processedAt: note.processedAt ?? '',
  issueKeys: note.issueKeys ?? [],
  createdAt: note.createdAt ?? '',
})

/** Сколько заметок держим на экране: лента справа, дальше — мит-режим */
const NOTES_LIMIT = 50

/**
 * Лента заметок. Сессия сужает её до одной встречи — так её читает мит-режим;
 * без неё приезжают свои личные плюс заметки всех встреч, где человек был.
 */
export const listNotes = async (sessionId = ''): Promise<Note[]> => {
  const { data } = await planApi.planServiceListNotes(
    false,
    '1',
    String(NOTES_LIMIT),
    sessionId || undefined,
  )

  return (data.notes ?? []).map(toNote)
}

export const createNote = async (body: string, sessionId = ''): Promise<Note | null> => {
  const { data } = await planApi.planServiceCreateNote({
    bodyMd: body,
    sessionId: sessionId || undefined,
  })

  return data.id ? toNote(data) : null
}

/** Что правим в заметке: текст, пометку разбора и ключи вышедших из неё задач */
export interface NotePatch {
  body?: string
  processed?: boolean
  issueKeys?: string[]
}

export const updateNote = async (id: string, patch: NotePatch): Promise<Note | null> => {
  const { data } = await planApi.planServiceUpdateNote(id, {
    bodyMd: patch.body,
    processed: patch.processed,
    issueKeys: patch.issueKeys,
  })

  return data.id ? toNote(data) : null
}

export const deleteNote = async (id: string): Promise<void> => {
  await planApi.planServiceDeleteNote(id)
}

const startOfDay = (date: Date): number =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()

/**
 * Время заметки — «14:05 · сегодня» (макет 9-5).
 *
 * Часы впереди дня, а не наоборот, как у события задачи: заметки за один
 * разговор ложатся подряд одним днём, и различает их именно время. У давней дата
 * заменяет слово, но час остаётся на месте — колонка не должна прыгать.
 */
export const formatNoteStamp = (
  iso: string,
  locale: string,
  labels: { today: string; yesterday: string },
  now = new Date(),
): string => {
  if (!iso) return ''

  const date = new Date(iso)

  if (Number.isNaN(date.getTime())) return ''

  const days = Math.round((startOfDay(now) - startOfDay(date)) / 86_400_000)
  const time = new Intl.DateTimeFormat(intlLocale(locale), {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)

  if (days === 0) return `${time} · ${labels.today}`
  if (days === 1) return `${time} · ${labels.yesterday}`

  return `${time} · ${formatDueDate(iso, locale, now)}`
}
